import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
// import { notification } from 'antd';
import { showErrorNotification } from 'ts/helpers/showErrorNotification';

const DEFAULT_SERVER_ERROR_MESSAGE = 'Server Error';
const DEFAULT_SERVER_ERROR_DESCRIPTION = 'The server encountered an error. Please contact support.';

class Request {
  private axios: AxiosInstance;

  private static extractErrorMessage(data: any): string | null {
    if (!data) return null;
    if (typeof data === 'string') return data;
    if (typeof data === 'object') {
      return data.message || data.error || null;
    }
    return null;
  }

  private static getErrorMessageAndDescription(error: any): { message: string, description?: string } {
    const status = error.response?.status;
    if (status === 500) {
      return {
        message: DEFAULT_SERVER_ERROR_MESSAGE,
        description: DEFAULT_SERVER_ERROR_DESCRIPTION,
      };
    }
    if (!status && error.code) {
      return { message: error.message };
    }
    const extractErrorMessage = this.extractErrorMessage(error.response?.data);
    return extractErrorMessage
      ? { message: extractErrorMessage }
      : {
        message: DEFAULT_SERVER_ERROR_MESSAGE,
        description: DEFAULT_SERVER_ERROR_DESCRIPTION,
      };
  }

  constructor() {
    this.axios = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      // ToDo: only for dev env. Update once backand url is fixed (devops)
      withCredentials: true,
    });
    this.axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const config: AxiosRequestConfig = error.config;
        const status = error.response?.status;

        if (config.skipNotificationsForErrors && status && config.skipNotificationsForErrors.includes(status)) {
          throw error;
        }

        const { message, description } =  Request.getErrorMessageAndDescription(error);
        showErrorNotification(message, description ?? '');

        throw error;
      }
    );
  }

  async get<T>(
    path: string,
    config?: AxiosRequestConfig
  ): Promise<{ response: T | null; error: any }> {
    try {
      const { data } = await this.axios.get<T>(path, config);
      return { response: data, error: null };
    } catch (error: any) {
      return { response: null, error: error.response || 'Unknown Error' };
    }
  }

  async post<T>(
    path: string,
    payload?: any,
    config?: AxiosRequestConfig
  ): Promise<any> {
    try {
      const { data } = await this.axios.post<T>(
        path,
        payload ?? payload,
        config
      );
      return { response: data, error: null };
    } catch (error: any) {
      return { response: null, error: error.response || 'Unknown Error' };
    }
  }

  async put<T>(
    path: string,
    payload: any,
    config?: AxiosRequestConfig
  ): Promise<any> {
    try {
      const { data } = await this.axios.put<T>(path, payload, config);
      return { response: data, error: null };
    } catch (error: any) {
      return { response: null, error: error.response || 'Unknown Error' };
    }
  }
}

export const request = new Request();
